import * as wand from '$lib/api/wand';
import type {
  WandEnumInfo,
  WandExecutorContract,
  WandServiceSchema,
  WandStructInfo,
  WandTypeRef,
} from '@helinks/wand-api-thrift/wand_types';
import { typeRefToString as _typeRefToString, typeRefFromString, typeRefStableKey } from '$lib/utils/typeref';
import { WandNamedTypeRef, WandTypeRef as TypeRef } from '@helinks/wand-api-thrift/wand_types';
import { getContractsState } from '$lib/stores/executor_contracts.svelte';
import { describeType } from '$lib/types/typeDescriptors';

export type SchemaCatalogState =
  | { status: 'idle'; schemas: null }
  | { status: 'loading'; schemas: WandServiceSchema[] | null }
  | { status: 'ready'; schemas: WandServiceSchema[] }
  | { status: 'error'; schemas: WandServiceSchema[] | null; error: unknown };

/** Human-readable rendering of a TypeRef (e.g. "list<SignalItemInfo>"). */
export function typeRefToString(ref: WandTypeRef | undefined | null): string {
  if (!ref) return '';
  return _typeRefToString(ref);
}

let state = $state<SchemaCatalogState>({ status: 'idle', schemas: null });

export function getSchemaState(): SchemaCatalogState {
  return state;
}

function requireSchemas(): WandServiceSchema[] {
  if (state.status === 'ready') return state.schemas;
  if (state.status === 'error') throw state.error;
  throw new Error(`Schema catalog is ${state.status}`);
}

function requireContracts(): WandExecutorContract[] {
  const contracts = getContractsState();
  if (contracts.status === 'ready') return contracts.contracts;
  if (contracts.status === 'error') throw contracts.error;
  throw new Error(`Executor contract catalog is ${contracts.status}`);
}

function allSchemas(): WandServiceSchema[] {
  return [...requireSchemas(), ...requireContracts().map((contract) => contract.schema)];
}
function availableSchemas(): WandServiceSchema[] | null {
  const schemas = state.schemas;
  const contracts = getContractsState().contracts;
  if (schemas === null || contracts === null) return null;
  return [...schemas, ...contracts.map((contract) => contract.schema)];
}

const PRIMITIVE_TYPES = ['bool', 'i8', 'i16', 'i32', 'i64', 'double', 'string', 'binary', 'python_code'];

export interface TypeOption {
  value: string;
  typeRef: WandTypeRef;
  label: string;
  category: string;
  description: string;
  kind: 'primitive' | 'struct' | 'enum';
  fields?: Array<{ name: string; type?: string; doc?: string }>;
}

// thrift gen drops `//` line comments; descriptions live in mcp.doc annotations.
export function docOf(x: { doc?: string | null; annotations?: Record<string, string> | null }): string | undefined {
  return x.annotations?.['mcp.doc'] ?? x.doc ?? undefined;
}

// Mirrors backend thrift_coercion.enum_member_docs: member-level annotations
// drop in JSON gen, so per-value docs live on the enum as `mcp.doc.<NAME>`.
function enumMemberDocs(en: WandEnumInfo): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(en.annotations ?? {})) {
    if (k.startsWith('mcp.doc.')) out[k.slice('mcp.doc.'.length)] = v;
  }
  return out;
}

export function getAvailableTypeOptions(): TypeOption[] {
  const schemas = requireSchemas();
  const contracts = requireContracts();
  const out: TypeOption[] = [];
  const seen = new Set<string>();

  const pushPrimitive = (name: string) => {
    if (seen.has(name)) return;
    seen.add(name);
    const typeRef = typeRefFromString(name, namespaceOfNamedType);
    out.push({ value: typeRefStableKey(typeRef), typeRef, label: name, category: 'Primitive', kind: 'primitive', description: describeType(name) });
  };
  const pushStruct = (st: WandStructInfo, category: string, namespace: string) => {
    const typeRef = new TypeRef({ structRef: new WandNamedTypeRef({ namespace, name: st.name }) });
    const value = typeRefStableKey(typeRef);
    if (seen.has(value)) return;
    seen.add(value);
    out.push({
      value, typeRef,
      label: st.name,
      category,
      kind: 'struct',
      description: docOf(st) ?? '',
      fields: st.fields.map((f) => ({
        name: f.name,
        type: _typeRefToString(f.type),
        doc: docOf(f),
      })),
    });
  };
  const pushEnum = (en: WandEnumInfo, category: string, namespace: string) => {
    const typeRef = new TypeRef({ enumRef: new WandNamedTypeRef({ namespace, name: en.name }) });
    const value = typeRefStableKey(typeRef);
    if (seen.has(value)) return;
    seen.add(value);
    const memberDocs = enumMemberDocs(en);
    out.push({
      value, typeRef,
      label: en.name,
      category,
      kind: 'enum',
      description: docOf(en) ?? '',
      fields: en.values.map((v) => ({ name: v.name, doc: memberDocs[v.name] ?? docOf(v) })),
    });
  };

  for (const p of PRIMITIVE_TYPES) pushPrimitive(p);
  for (const schema of schemas) {
    const category = schema.namespace === 'helinks.wand' ? 'Wand' : `Schema: ${schema.namespace}`;
    for (const st of schema.structs) pushStruct(st, category, schema.namespace);
    for (const en of schema.enums) pushEnum(en, category, schema.namespace);
  }
  for (const contract of contracts) {
    const schema = contract.schema;
    const category = `Contract: ${contract.serviceName}`;
    for (const st of schema.structs) pushStruct(st, category, schema.namespace);
    for (const en of schema.enums) pushEnum(en, category, schema.namespace);
  }
  return out;
}
export function findStructInfo(name: string, namespace?: string): WandStructInfo | null {
  const schemas = availableSchemas();
  if (!schemas) return null;
  for (const schema of schemas) {
    if (namespace !== undefined && schema.namespace !== namespace) continue;
    const found = schema.structs.find((struct) => struct.name === name);
    if (found) return found;
  }
  return null;
}
export function findEnumInfo(name: string, namespace?: string): WandEnumInfo | null {
  const schemas = availableSchemas();
  if (!schemas) return null;
  for (const schema of schemas) {
    if (namespace !== undefined && schema.namespace !== namespace) continue;
    const found = schema.enums.find((enumeration) => enumeration.name === name);
    if (found) return found;
  }
  return null;
}

export function getStructInfo(name: string, namespace?: string): WandStructInfo {
  if (!name) throw new Error('Declared struct name is required');
  requireSchemas();
  requireContracts();
  const struct = findStructInfo(name, namespace);
  if (!struct) throw new Error(`Declared struct ${namespace ? `${namespace}.` : ''}${name} is unavailable`);
  return struct;
}

export function getEnumInfo(name: string, namespace?: string): WandEnumInfo {
  if (!name) throw new Error('Declared enum name is required');
  requireSchemas();
  requireContracts();
  const enumeration = findEnumInfo(name, namespace);
  if (!enumeration) throw new Error(`Declared enum ${namespace ? `${namespace}.` : ''}${name} is unavailable`);
  return enumeration;
}

/** Optional namespace discovery for bare type-picker labels. */
export function namespaceOfNamedType(name: string): string | null {
  for (const schema of allSchemas()) {
    if (schema.structs.some((struct) => struct.name === name)) return schema.namespace;
    if (schema.enums.some((enumeration) => enumeration.name === name)) return schema.namespace;
  }
  return null;
}

export async function fetchSchema(): Promise<void> {
  const schemas = state.schemas;
  state = { status: 'loading', schemas };
  try {
    state = { status: 'ready', schemas: await wand.listSchemas() };
  } catch (error) {
    state = { status: 'error', schemas, error };
    throw error;
  }
}